import React, { useState, ReactNode } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Button
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

interface ReactionItem {
  tag: string;
  text: string;
}

interface ReactionsCardProps {
  reactions: ReactionItem[];
  positivePct?: number;
  neutralPct?: number;
  negativePct?: number;
  titleComponent?: ReactNode;
}

const ReactionsCard: React.FC<ReactionsCardProps> = ({ 
  reactions, 
  positivePct = 33.3, 
  neutralPct = 33.3, 
  negativePct = 33.3,
  titleComponent
}) => {
  // Initial state shows all sentiment types
  const allFilters = ['positive', 'neutral', 'negative'];
  const [activeFilters, setActiveFilters] = useState<string[]>(allFilters);

  // Custom click handler for each sentiment button
  const handleSentimentClick = (sentiment: string) => () => {
    if (activeFilters.length === 1 && activeFilters[0] === sentiment) {
      // If this sentiment is the only one showing, reset to all sentiments
      setActiveFilters(allFilters);
    } else {
      // Otherwise show only this sentiment
      setActiveFilters([sentiment]);
    }
  };

  // Create an array of sentiment types sorted by percentage (highest first)
  const sortedSentiments = [
    { tag: 'positive', pct: positivePct },
    { tag: 'neutral', pct: neutralPct },
    { tag: 'negative', pct: negativePct }
  ].sort((a, b) => b.pct - a.pct);
  
  // Group reactions by sentiment
  const groupedReplies: Record<string, ReactionItem[]> = {
    'positive': [],
    'neutral': [],
    'negative': []
  };
  
  // Map emoji tags to sentiment tags and populate groups
  reactions.forEach(reply => {
    let sentimentTag = reply.tag;
    
    // Map emoji tags to sentiment tags (for backwards compatibility)
    if (reply.tag === '👍') {
      sentimentTag = 'positive';
    } else if (reply.tag === '😐') {
      sentimentTag = 'neutral';
    } else if (reply.tag === '👎') {
      sentimentTag = 'negative';
    }
    
    if (groupedReplies[sentimentTag]) {
      groupedReplies[sentimentTag].push(reply);
    }
  });
  
  // Create a flat array in the desired order, filtered by active filters
  const filteredReplies = sortedSentiments
    .filter(sentiment => activeFilters.includes(sentiment.tag))
    .flatMap(sentiment => groupedReplies[sentiment.tag]);

  return (
    <Card elevation={3}>
      <CardContent>
        {titleComponent || (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" component="div">
              Sample Reactions
            </Typography>
            <Tooltip title="AI-generated sample reactions that your content might receive. These are filtered by sentiment type (positive, neutral, negative) based on our analysis." arrow>
              <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
        {/* Filter buttons */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Filter by sentiment:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={activeFilters.includes('positive') ? "contained" : "outlined"}
              onClick={handleSentimentClick('positive')}
              sx={{ 
                backgroundColor: activeFilters.includes('positive') && activeFilters.length === 1 
                  ? 'rgba(6, 214, 160, 0.8)' 
                  : activeFilters.includes('positive') 
                    ? 'rgba(6, 214, 160, 0.2)' 
                    : 'inherit',
                color: activeFilters.includes('positive') ? 'inherit' : 'rgba(6, 214, 160, 0.8)',
                '&:hover': {
                  backgroundColor: activeFilters.includes('positive') 
                    ? 'rgba(6, 214, 160, 0.3)' 
                    : 'rgba(6, 214, 160, 0.1)'
                }
              }}
            >
              Positive
            </Button>
            <Button
              variant={activeFilters.includes('neutral') ? "contained" : "outlined"}
              onClick={handleSentimentClick('neutral')}
              sx={{ 
                backgroundColor: activeFilters.includes('neutral') && activeFilters.length === 1 
                  ? 'rgba(170, 170, 170, 0.8)' 
                  : activeFilters.includes('neutral') 
                    ? 'rgba(170, 170, 170, 0.2)' 
                    : 'inherit',
                color: activeFilters.includes('neutral') ? 'inherit' : 'rgba(170, 170, 170, 0.8)',
                '&:hover': {
                  backgroundColor: activeFilters.includes('neutral') 
                    ? 'rgba(170, 170, 170, 0.3)' 
                    : 'rgba(170, 170, 170, 0.1)'
                }
              }}
            >
              Neutral
            </Button>
            <Button
              variant={activeFilters.includes('negative') ? "contained" : "outlined"}
              onClick={handleSentimentClick('negative')}
              sx={{ 
                backgroundColor: activeFilters.includes('negative') && activeFilters.length === 1 
                  ? 'rgba(255, 107, 107, 0.8)' 
                  : activeFilters.includes('negative') 
                    ? 'rgba(255, 107, 107, 0.2)' 
                    : 'inherit',
                color: activeFilters.includes('negative') ? 'inherit' : 'rgba(255, 107, 107, 0.8)',
                '&:hover': {
                  backgroundColor: activeFilters.includes('negative') 
                    ? 'rgba(255, 107, 107, 0.3)' 
                    : 'rgba(255, 107, 107, 0.1)'
                }
              }}
            >
              Negative
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2, maxHeight: '500px', overflowY: 'auto' }}>
          <Stack spacing={1}>
            {filteredReplies.length > 0 ? (
              filteredReplies.map((reply, index) => {
                // Determine the sentiment for this reply
                const isPositive = reply.tag === 'positive' || reply.tag === '👍';
                const isNegative = reply.tag === 'negative' || reply.tag === '👎';
                const isNeutral = reply.tag === 'neutral' || reply.tag === '😐';
                
                return (
                  <Paper 
                    key={index} 
                    elevation={1} 
                    sx={{ 
                      p: 2,
                      bgcolor: isPositive
                        ? 'rgba(6, 214, 160, 0.15)' 
                        : isNegative
                          ? 'rgba(255, 107, 107, 0.15)' 
                          : 'rgba(170, 170, 170, 0.15)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box 
                        sx={{ 
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: isPositive
                            ? 'rgba(6, 214, 160, 1)' 
                            : isNegative
                              ? 'rgba(255, 107, 107, 1)' 
                              : 'rgba(170, 170, 170, 1)',
                          mt: '6px',
                          mr: 2,
                          flexShrink: 0
                        }}
                      />
                      <Typography variant="body1">
                        {reply.text}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Typography>No reactions to display for selected filters</Typography>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReactionsCard; 